import { useEffect, useSyncExternalStore } from "react";
import { validatePrelaunchBypassGrant } from "./prelaunchBypass";

const DEFAULT_SITE_MODE = "live";
const PRELAUNCH_SITE_MODE = "prelaunch";
const PRELAUNCH_ALLOWED_PATHS = new Set([
  "/",
  "/early-access",
  "/auth",
  "/auth/login",
  "/auth/magic-link",
  "/auth/complete",
  "/auth/reset-password",
  "/auth/callback",
  "/auth/logout",
  "/auth/email-change/pending",
]);
const PRELAUNCH_BYPASS_GRANT_SESSION_KEY = "refanno_prelaunch_bypass_grant";
const PRELAUNCH_PENDING_PATH_SESSION_KEY = "refanno_prelaunch_pending_path";
const PRELAUNCH_LEAD_SOURCE = "website:refanno-prelaunch";
const PRELAUNCH_BYPASS_EVENT = "refanno:prelaunch-bypass-change";
const PRELAUNCH_ACCESS_STATUS = {
  LOCKED: "locked",
  CHECKING: "checking",
  UNLOCKED: "unlocked",
};

const SERVER_SNAPSHOT = {
  grant: "",
  status: PRELAUNCH_ACCESS_STATUS.LOCKED,
};

let prelaunchAccessSnapshot = SERVER_SNAPSHOT;
let inflightGrantValidation = null;

function normalizeString(value) {
  return String(value || "").trim();
}

function normalizePath(pathname) {
  const value = normalizeString(pathname);
  if (!value) return "/";
  return value.startsWith("/") ? value : `/${value}`;
}

function isPrelaunchAllowedPath(pathname) {
  return PRELAUNCH_ALLOWED_PATHS.has(normalizePath(pathname));
}

function resolveSessionStorage(storage) {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch (_error) {
    return null;
  }
}

function setSessionValue(key, value, storage = resolveSessionStorage()) {
  if (!storage) return;
  storage.setItem(key, value);
}

function getSessionValue(key, storage = resolveSessionStorage()) {
  if (!storage) return "";
  return normalizeString(storage.getItem(key));
}

function removeSessionValue(key, storage = resolveSessionStorage()) {
  if (!storage) return;
  storage.removeItem(key);
}

function emitPrelaunchBypassChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(PRELAUNCH_BYPASS_EVENT));
}

function updatePrelaunchAccessSnapshot(nextSnapshot) {
  const grant = normalizeString(nextSnapshot?.grant);
  const status = normalizeString(nextSnapshot?.status);

  if (
    prelaunchAccessSnapshot.grant === grant &&
    prelaunchAccessSnapshot.status === status
  ) {
    return;
  }

  prelaunchAccessSnapshot = { grant, status };
  emitPrelaunchBypassChange();
}

function getStoredPrelaunchGrant(storage = resolveSessionStorage()) {
  return getSessionValue(PRELAUNCH_BYPASS_GRANT_SESSION_KEY, storage);
}

function syncPrelaunchAccessSnapshot(storage = resolveSessionStorage()) {
  if (!isPrelaunchSiteMode(process.env)) {
    updatePrelaunchAccessSnapshot({
      grant: "",
      status: PRELAUNCH_ACCESS_STATUS.UNLOCKED,
    });
    return;
  }

  const storedGrant = getStoredPrelaunchGrant(storage);
  if (!storedGrant) {
    updatePrelaunchAccessSnapshot({
      grant: "",
      status: PRELAUNCH_ACCESS_STATUS.LOCKED,
    });
    return;
  }

  if (prelaunchAccessSnapshot.grant !== storedGrant) {
    updatePrelaunchAccessSnapshot({
      grant: storedGrant,
      status: PRELAUNCH_ACCESS_STATUS.LOCKED,
    });
  }
}

function subscribeToPrelaunchBypass(listener) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const handleChange = () => {
    syncPrelaunchAccessSnapshot();
    listener();
  };

  window.addEventListener(PRELAUNCH_BYPASS_EVENT, handleChange);
  window.addEventListener("storage", handleChange);
  syncPrelaunchAccessSnapshot();

  return () => {
    window.removeEventListener(PRELAUNCH_BYPASS_EVENT, handleChange);
    window.removeEventListener("storage", handleChange);
  };
}

export function getSiteMode(env = process.env) {
  const rawMode = normalizeString(env.REACT_APP_SITE_MODE || env.SITE_MODE)
    .toLowerCase();
  if (rawMode === PRELAUNCH_SITE_MODE) return PRELAUNCH_SITE_MODE;
  return DEFAULT_SITE_MODE;
}

export function isPrelaunchSiteMode(env = process.env) {
  return getSiteMode(env) === PRELAUNCH_SITE_MODE;
}

export function hasPrelaunchBypass(storage = resolveSessionStorage()) {
  const storedGrant = getStoredPrelaunchGrant(storage);
  return (
    Boolean(storedGrant) &&
    prelaunchAccessSnapshot.status === PRELAUNCH_ACCESS_STATUS.UNLOCKED &&
    prelaunchAccessSnapshot.grant === storedGrant
  );
}

export function enablePrelaunchBypass(grant, storage = resolveSessionStorage()) {
  const normalizedGrant = normalizeString(grant);
  if (!normalizedGrant) return;

  setSessionValue(PRELAUNCH_BYPASS_GRANT_SESSION_KEY, normalizedGrant, storage);
  updatePrelaunchAccessSnapshot({
    grant: normalizedGrant,
    status: PRELAUNCH_ACCESS_STATUS.UNLOCKED,
  });
}

export function clearPrelaunchBypass(storage = resolveSessionStorage()) {
  removeSessionValue(PRELAUNCH_BYPASS_GRANT_SESSION_KEY, storage);
  inflightGrantValidation = null;
  updatePrelaunchAccessSnapshot({
    grant: "",
    status: PRELAUNCH_ACCESS_STATUS.LOCKED,
  });
}

export function rememberPrelaunchPendingPath(
  pathname,
  storage = resolveSessionStorage(),
) {
  const normalizedPath = normalizePath(pathname);
  if (normalizedPath === "/") {
    removeSessionValue(PRELAUNCH_PENDING_PATH_SESSION_KEY, storage);
    return;
  }
  setSessionValue(PRELAUNCH_PENDING_PATH_SESSION_KEY, normalizedPath, storage);
}

export function consumePrelaunchPendingPath(
  storage = resolveSessionStorage(),
) {
  const nextPath =
    getSessionValue(PRELAUNCH_PENDING_PATH_SESSION_KEY, storage) || "/";
  removeSessionValue(PRELAUNCH_PENDING_PATH_SESSION_KEY, storage);
  return normalizePath(nextPath);
}

export function shouldGatePathname(
  pathname,
  env = process.env,
  storage = resolveSessionStorage(),
) {
  if (!isPrelaunchSiteMode(env)) return false;
  if (hasPrelaunchBypass(storage)) return false;
  return !isPrelaunchAllowedPath(pathname);
}

export function shouldUsePrelaunchLanding(
  pathname,
  env = process.env,
  storage = resolveSessionStorage(),
) {
  return (
    isPrelaunchSiteMode(env) &&
    !hasPrelaunchBypass(storage) &&
    normalizePath(pathname) === "/"
  );
}

export function resolveLandingLeadSource() {
  return PRELAUNCH_LEAD_SOURCE;
}

export async function ensurePrelaunchBypass(storage = resolveSessionStorage()) {
  if (!isPrelaunchSiteMode(process.env)) {
    updatePrelaunchAccessSnapshot({
      grant: "",
      status: PRELAUNCH_ACCESS_STATUS.UNLOCKED,
    });
    return true;
  }

  const storedGrant = getStoredPrelaunchGrant(storage);
  if (!storedGrant) {
    updatePrelaunchAccessSnapshot({
      grant: "",
      status: PRELAUNCH_ACCESS_STATUS.LOCKED,
    });
    return false;
  }

  if (
    prelaunchAccessSnapshot.grant === storedGrant &&
    prelaunchAccessSnapshot.status === PRELAUNCH_ACCESS_STATUS.UNLOCKED
  ) {
    return true;
  }

  if (inflightGrantValidation?.grant === storedGrant) {
    return inflightGrantValidation.promise;
  }

  updatePrelaunchAccessSnapshot({
    grant: storedGrant,
    status: PRELAUNCH_ACCESS_STATUS.CHECKING,
  });

  const validationPromise = validatePrelaunchBypassGrant(storedGrant)
    .then(() => {
      if (getStoredPrelaunchGrant(storage) !== storedGrant) {
        syncPrelaunchAccessSnapshot(storage);
        return false;
      }

      updatePrelaunchAccessSnapshot({
        grant: storedGrant,
        status: PRELAUNCH_ACCESS_STATUS.UNLOCKED,
      });
      return true;
    })
    .catch(() => {
      if (getStoredPrelaunchGrant(storage) === storedGrant) {
        clearPrelaunchBypass(storage);
      } else {
        syncPrelaunchAccessSnapshot(storage);
      }
      return false;
    })
    .finally(() => {
      if (inflightGrantValidation?.grant === storedGrant) {
        inflightGrantValidation = null;
      }
    });

  inflightGrantValidation = {
    grant: storedGrant,
    promise: validationPromise,
  };

  return validationPromise;
}

export function usePrelaunchBypassState() {
  return useSyncExternalStore(
    subscribeToPrelaunchBypass,
    () => prelaunchAccessSnapshot,
    () => SERVER_SNAPSHOT,
  );
}

export function useHasPrelaunchBypass() {
  const snapshot = usePrelaunchBypassState();
  return snapshot.status === PRELAUNCH_ACCESS_STATUS.UNLOCKED;
}

export function usePrelaunchGateState() {
  const snapshot = usePrelaunchBypassState();

  useEffect(() => {
    syncPrelaunchAccessSnapshot();

    if (!isPrelaunchSiteMode(process.env)) return;
    if (!snapshot.grant) return;
    if (snapshot.status === PRELAUNCH_ACCESS_STATUS.UNLOCKED) return;
    if (snapshot.status === PRELAUNCH_ACCESS_STATUS.CHECKING) return;

    void ensurePrelaunchBypass();
  }, [snapshot.grant, snapshot.status]);

  return snapshot;
}

export function useShouldUsePrelaunchLanding(pathname) {
  const snapshot = usePrelaunchGateState();
  const isRootPath = normalizePath(pathname) === "/";

  return {
    isChecking:
      isRootPath && snapshot.status === PRELAUNCH_ACCESS_STATUS.CHECKING,
    shouldUseLanding:
      isPrelaunchSiteMode(process.env) &&
      snapshot.status !== PRELAUNCH_ACCESS_STATUS.UNLOCKED &&
      isRootPath,
  };
}
